import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Slider,
    Switch,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconPalette,
    IconSearch,
    IconPlus,
    IconDeviceFloppy,
    IconTrash,
    IconX,
    IconChevronRight,
    IconMoon,
    IconSun,
    IconLayoutSidebar,
    IconTypography,
    IconBrush,
    IconStar,
    IconCircleCheck
} from '@tabler/icons';
import api from '../../../../services/api';
import { motion } from 'framer-motion';

import axios from 'axios';

const MotionCard = motion(Card);

// const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// const api = axios.create({
//     baseURL: API_URL
// });

// api.interceptors.request.use((config) => {
//     const token = localStorage.getItem('access_token');

//     if (token) {
//         config.headers.Authorization = `Bearer ${token}`;
//     }

//     return config;
// });

const CUSTOMIZATION_CATEGORIES = {
    appearance: {
        label: 'Aparência',
        icon: IconPalette,
        color: '#7c3aed',
        gradient: 'linear-gradient(135deg,#7c3aed,#a855f7)'
    },

    layout: {
        label: 'Layout',
        icon: IconLayoutSidebar,
        color: '#2563eb',
        gradient: 'linear-gradient(135deg,#2563eb,#60a5fa)'
    },

    typography: {
        label: 'Tipografia',
        icon: IconTypography,
        color: '#ea580c',
        gradient: 'linear-gradient(135deg,#ea580c,#fb923c)'
    },

    animation: {
        label: 'Animações',
        icon: IconStar,
        color: '#16a34a',
        gradient: 'linear-gradient(135deg,#16a34a,#22c55e)'
    }
};

const DEFAULT_FORM = {
    name: '',
    category: 'appearance',
    mode: 'dark',
    primary_color: '#7c3aed',
    border_radius: 18,
    compact_mode: false,
    animations: true,
    font_family: 'Inter'
};

const SystemCustomization = () => {
    const theme = useTheme();

    const [customizations, setCustomizations] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');

    const [status, setStatus] = useState({
        type: '',
        message: ''
    });

    const [openModal, setOpenModal] = useState(false);

    const [editingItem, setEditingItem] = useState(null);

    const [form, setForm] = useState(DEFAULT_FORM);

    useEffect(() => {
        loadCustomizations();
    }, []);

    const loadCustomizations = async () => {
        setLoading(true);

        try {
            const response = await api.get('v1/system/customizations/');

            setCustomizations(response.data?.customizations || []);
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao carregar customizações.'
            });
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        if (!search.trim()) {
            return customizations;
        }

        const term = search.toLowerCase();

        return customizations.filter((item) => item.name?.toLowerCase().includes(term) || item.category?.toLowerCase().includes(term));
    }, [search, customizations]);

    const handleOpenCreate = () => {
        setEditingItem(null);

        setForm(DEFAULT_FORM);

        setOpenModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);

        setForm({
            name: item.name || '',
            category: item.category || 'appearance',
            mode: item.mode || 'dark',
            primary_color: item.primary_color || '#7c3aed',
            border_radius: item.border_radius || 18,
            compact_mode: item.compact_mode ?? false,
            animations: item.animations ?? true,
            font_family: item.font_family || 'Inter'
        });

        setOpenModal(true);
    };

    const handleSave = async () => {
        try {
            if (editingItem) {
                await api.patch(`v1/system/customizations/${editingItem.id}/`, form);

                setStatus({
                    type: 'success',
                    message: 'Customização atualizada.'
                });
            } else {
                await api.post('/api/v1/system/customizations/', form);

                setStatus({
                    type: 'success',
                    message: 'Customização criada.'
                });
            }

            setOpenModal(false);

            loadCustomizations();
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao salvar customização.'
            });
        }
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Deseja realmente excluir esta customização?');

        if (!confirmDelete) {
            return;
        }

        try {
            await api.delete(`/api/v1/system/customizations/${id}/`);

            setCustomizations((prev) => prev.filter((item) => item.id !== id));

            setStatus({
                type: 'success',
                message: 'Customização removida.'
            });
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao excluir customização.'
            });
        }
    };

    return (
        <Box>
            {/* HERO */}

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '30px',
                    p: {
                        xs: 3,
                        md: 5
                    },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#1e1b4b 100%)'
                            : 'linear-gradient(135deg,#faf5ff 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.08)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(124,58,237,0.12))',
                        top: -120,
                        right: -80,
                        filter: 'blur(50px)'
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Chip
                        icon={<IconBrush size={14} />}
                        label="Personalização"
                        sx={{
                            mb: 2,
                            fontWeight: 700
                        }}
                    />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: '2rem',
                                md: '3rem'
                            },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#7c3aed,#2563eb)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Customização do Sistema
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 720,
                            lineHeight: 1.8,
                            mb: 4
                        }}
                    >
                        Configure temas, cores, tipografia, animações e toda experiência visual do seu CRM e chatbot.
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap'
                        }}
                    >
                        <OutlinedInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar configuração..."
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            }
                            sx={{
                                flex: 1,
                                minWidth: 280,
                                height: 56,
                                borderRadius: '18px',
                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={handleOpenCreate}
                            sx={{
                                height: 56,
                                px: 3,
                                borderRadius: '18px',
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg,#7c3aed,#2563eb)',
                                boxShadow: '0 10px 25px rgba(124,58,237,0.25)'
                            }}
                        >
                            Nova Configuração
                        </Button>
                    </Box>
                </Box>
            </Box>

            {status.message && (
                <Alert
                    severity={status.type}
                    sx={{
                        mb: 3,
                        borderRadius: '16px'
                    }}
                >
                    {status.message}
                </Alert>
            )}

            {/* GRID */}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 10
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredItems.map((item, index) => {
                        const config = CUSTOMIZATION_CATEGORIES[item.category] || CUSTOMIZATION_CATEGORIES.appearance;

                        const Icon = config.icon;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                                <MotionCard
                                    initial={{
                                        opacity: 0,
                                        y: 20
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0
                                    }}
                                    transition={{
                                        delay: index * 0.04
                                    }}
                                    whileHover={{
                                        y: -6
                                    }}
                                    sx={{
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        height: '100%',
                                        border:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(15,23,42,0.06)',
                                        background:
                                            theme.palette.mode === 'dark' ? 'linear-gradient(180deg,#111827 0%,#0f172a 100%)' : '#fff',
                                        boxShadow:
                                            theme.palette.mode === 'dark'
                                                ? '0 10px 30px rgba(0,0,0,0.35)'
                                                : '0 10px 30px rgba(15,23,42,0.06)'
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleOpenEdit(item)}
                                        sx={{
                                            height: '100%'
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                p: 3
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 2
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: '18px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: config.gradient,
                                                        boxShadow: `0 10px 25px ${config.color}33`
                                                    }}
                                                >
                                                    <Icon size={30} color="#fff" />
                                                </Box>

                                                <Chip
                                                    size="small"
                                                    icon={<IconCircleCheck size={12} />}
                                                    label="Ativo"
                                                    color="success"
                                                    sx={{
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1
                                                }}
                                            >
                                                {item.name}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    mb: 2
                                                }}
                                            >
                                                {config.label}
                                            </Typography>

                                            <Divider
                                                sx={{
                                                    my: 2
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.2
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}
                                                >
                                                    {item.mode === 'dark' ? <IconMoon size={16} /> : <IconSun size={16} />}

                                                    <Typography variant="caption">Modo {item.mode}</Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 14,
                                                            height: 14,
                                                            borderRadius: '50%',
                                                            background: item.primary_color
                                                        }}
                                                    />

                                                    <Typography variant="caption">{item.primary_color}</Typography>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    mt: 3,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    Abrir configuração
                                                </Typography>

                                                <IconChevronRight size={18} />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* MODAL */}

            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: '26px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 800
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <IconPalette size={22} />

                        {editingItem ? 'Editar Customização' : 'Nova Customização'}
                    </Box>

                    <IconButton onClick={() => setOpenModal(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nome"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        name: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Categoria"
                                value={form.category}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        category: e.target.value
                                    })
                                }
                            >
                                {Object.entries(CUSTOMIZATION_CATEGORIES).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mb: 1.5,
                                    fontWeight: 700
                                }}
                            >
                                Tema do sistema
                            </Typography>

                            <FormControl>
                                <RadioGroup
                                    row
                                    value={form.mode}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            mode: e.target.value
                                        })
                                    }
                                >
                                    <FormControlLabel value="light" control={<Radio />} label="Light" />

                                    <FormControlLabel value="dark" control={<Radio />} label="Dark" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mb: 1.5,
                                    fontWeight: 700
                                }}
                            >
                                Cor principal
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                <input
                                    type="color"
                                    value={form.primary_color}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            primary_color: e.target.value
                                        })
                                    }
                                    style={{
                                        width: 70,
                                        height: 50,
                                        border: 'none',
                                        borderRadius: 12,
                                        background: 'transparent',
                                        cursor: 'pointer'
                                    }}
                                />

                                <Typography>{form.primary_color}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Fonte"
                                value={form.font_family}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        font_family: e.target.value
                                    })
                                }
                            >
                                <MenuItem value="Inter">Inter</MenuItem>

                                <MenuItem value="Poppins">Poppins</MenuItem>

                                <MenuItem value="Roboto">Roboto</MenuItem>

                                <MenuItem value="Montserrat">Montserrat</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mb: 2,
                                    fontWeight: 700
                                }}
                            >
                                Border Radius
                            </Typography>

                            <Slider
                                value={form.border_radius}
                                min={0}
                                max={40}
                                onChange={(_, value) =>
                                    setForm({
                                        ...form,
                                        border_radius: value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.compact_mode}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    compact_mode: e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Modo compacto"
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.animations}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    animations: e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Animações habilitadas"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        justifyContent: 'space-between'
                    }}
                >
                    {editingItem ? (
                        <Button
                            color="error"
                            startIcon={<IconTrash size={18} />}
                            onClick={() => {
                                setOpenModal(false);

                                handleDelete(editingItem.id);
                            }}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none'
                            }}
                        >
                            Excluir
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Button
                        variant="contained"
                        startIcon={<IconDeviceFloppy size={18} />}
                        onClick={handleSave}
                        sx={{
                            borderRadius: '16px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#7c3aed,#2563eb)',
                            boxShadow: '0 10px 25px rgba(124,58,237,0.25)'
                        }}
                    >
                        Salvar Configuração
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemCustomization;
